<template>
  <div class="calculator">
    <div class="calculator__wrap">
      <div class="calculator__amount">
        <span class="calculator__amount-label">Сумма микрокредита</span>
        <div class="calculator__amount-input-wrap">
          <input
            type="tel"
            v-model.lazy="currentValue"
            class="calculator__amount-input"
          />
          <span class="calculator__amount-icon">₸</span>
        </div>
      </div>
      <vue-slider
        ref="amountSlider"
        v-bind="optionsAmount"
        v-model="amount"
      ></vue-slider>
      <div class="calculator__slider-borders">
        <span class="calculator__slider-min">{{
          minAmount | numberFormat
        }}</span>
        <span class="calculator__slider-max">{{
          maxAmount | numberFormat
        }}</span>
      </div>

      <div class="calculator__settlement-type-wrap">
        <label class="calculator__settlement-wrap">
          <input
            type="radio"
            class="calculator__settlement-input"
            value="ANNUITY"
            v-model="creditType"
          />
          <span class="calculator__settlement-radio">Аннуитет</span>
        </label>
        <label class="calculator__settlement-wrap">
          <input
            type="radio"
            class="calculator__settlement-input"
            value="DIFFERENTIATED"
            v-model="creditType"
          />
          <span class="calculator__settlement-radio">Дифференцированный</span>
        </label>
      </div>

      <div class="calculator__info-wrap">
        <p class="calculator__info" v-if="isAnnuity">
          <span class="calculator__info-label">Ежемесячный платеж</span>
          <span class="calculator__amount-info-value"
            >{{ ANNUITY | numberFormat }} ₸</span
          >
        </p>
        <p class="calculator__info" v-else>
          <span class="calculator__info-label">Первый платеж</span>
          <span class="calculator__amount-info-value"
            >{{ DIFFERENTIATED | numberFormat }} ₸</span
          >
        </p>

        <p class="calculator__info">
          <span class="calculator__info-label">На срок</span>
          <span class="calculator__term-info-value">{{ monthCount }}</span>
        </p>
      </div>

      <vue-slider
        ref="termSlider"
        v-bind="optionsTerm"
        v-model="term"
      ></vue-slider>
      <div class="calculator__slider-borders">
        <span class="calculator__slider-min">{{ minTerm }} мес</span>
        <span class="calculator__slider-max">{{ maxTerm }} мес</span>
      </div>
      <slot name="button"></slot>
    </div>
    <div class="calculator__credit-info-wrap">
      <p class="calculator__credit-info">
        <span class="calculator__credit-info-label">Сумма микрокредита</span>
        <span class="calculator__credit-amount"
          >{{ amount | numberFormat }} ₸</span
        >
      </p>
      <p class="calculator__credit-info">
        <span class="calculator__credit-info-label">Переплата по кредиту</span>
        <span class="calculator__credit-date"
          >{{ OVERPAYMENT | numberFormat }} ₸</span
        >
      </p>
      <p class="calculator__credit-info">
        <span class="calculator__credit-info-label">Дата первого платежа</span>
        <span class="calculator__credit-date">{{ returnDate }}</span>
      </p>
      <p class="calculator__credit-info">
        <span class="calculator__credit-info-label">ГЭСВ</span>
        <span class="calculator__credit-date">{{ GESV }}%</span>
      </p>
    </div>

    <modal name="calculator-payment-schedule" :width="'320px'" :height="'auto'">
      <div class="modal-schedule">
        <div class="payment-scheduler">
          <h3 class="payment-scheduler__title">График платежей</h3>
          <ul class="payment-scheduler__list" v-if="paymentSchedule.length > 0">
            <li class="payment-scheduler__item" v-for="item in paymentSchedule">
              <span class="payment-scheduler__month"
                >{{ getFormat(item.date, "MMMM") | Capitalized }} '{{
                  getFormat(item.date, "YY")
                }}</span
              >
              <span class="payment-scheduler__center">{{
                getFormat(item.date, "DD")
              }}</span>
              <span class="payment-scheduler__amount"
                >{{ item.amount | ceil | numberFormat }} ₸</span
              >
            </li>
          </ul>

          <div class="payment-scheduler__loader" v-else>
            <div class="loader-popup">
              <svg
                id="coin"
                data-name="preloader"
                xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink"
                width="85"
                height="75"
                viewBox="0 0 170 150"
              >
                <circle id="spincoin" r="70" cx="90" cy="50" />
                <circle id="spincoin2" r="80" cx="85" cy="40" />
                <circle id="shadowcoin" r="80" cx="85" cy="40" />
              </svg>
              <p class="loader-text">Ваш график рассчитывается</p>
            </div>
          </div>
        </div>
      </div>
    </modal>
    <modal name="gesv-error" :width="'320px'" :height="'135px'">
      <div class="gesv-error-modal">
        <p class="gesv-error-modal__text">
          К сожалению, на данный момент мы не можем предоставить Вам микрокредит
          на выбранный срок. Попробуйте изменить срок и подать заявку заново.
        </p>
      </div>
    </modal>
  </div>
</template>

<script>
    import vueSlider from 'vue-slider-component'
    import each from 'lodash/each'
    import Helpers from '@/modules/Helpers';
    import moment from 'moment';
    import 'moment/locale/ru';
    import {createHelpers} from 'vuex-map-fields';
    import {STATUSES} from "@/components/statuses";

    const TARGET_GESV = 0.4598

    // --- Finance math (ported from files_for_frontend/finance.math.ts) ---

    function annuityPaymentCalc(principal, monthlyRate, months) {
        if (monthlyRate === 0) return principal / months;
        const pow = Math.pow(1 + monthlyRate, months);
        return (principal * monthlyRate * pow) / (pow - 1);
    }

    // principalPart = ROUND(P / n, 2); interest_m = ROUND(balance × i, 2); last month closes remainder
    function buildDifferentiatedSchedule(amount, monthlyRate, months) {
        const principalPart = Math.round(amount / months * 100) / 100;
        let balance = amount;
        const items = [];
        for (let m = 1; m <= months; m++) {
            const interest = Math.round(balance * monthlyRate * 100) / 100;
            const principal = m === months ? balance : Math.min(principalPart, balance);
            const payment = principal + interest;
            balance = Math.round((balance - principal) * 100) / 100;
            if (balance < 0) balance = 0;
            items.push({ payment, principal, interest, remainingBalance: balance });
        }
        return items;
    }

    // ---

    const {mapFields} = createHelpers({
        getterType: 'calculator/getField',
        mutationType: 'calculator/updateField',
    });

    moment.locale('ru');

    export default {
        name: 'Calculator',
        components: {
            vueSlider
        },
        props: {
            minAmount: {
                default: 100000
            },
            maxAmount: {
                default: 1000000
            },

            minTerm: {
                default: 1
            },
            maxTerm: {
                default: 12
            },
        },
        data() {
            return {
                paymentSchedule: [],
                optionsAmount: {
                    min: 100000,
                    max: 1000000,
                    interval: 5000,
                    value: 200000,
                    tooltip: false,
                    dotSize: 34,
                    bgStyle: {
                        "height": "16px",
                        "backgroundColor": "#E8E8E8"
                    },
                    processStyle: {
                        "height": "16px",
                        "backgroundColor": "#93278F"
                    },
                    sliderStyle: [
                        {
                            "backgroundColor": "rgba(0,0,0,0.5)"
                        },
                        {
                            "backgroundColor": "rgba(255,183,0,0.85)",
                            "top": '-9px'
                        },
                    ]
                },
                optionsTerm: {
                    reverse: false,
                    min: this.minTerm,
                    max: this.maxTerm,
                    value: 3,
                    piecewise: true,
                    tooltip: false,
                    dotSize: 34,
                    piecewiseStyle: {
                        "backgroundColor": "#ccc",
                        "visibility": "visible",
                        "width": "16px",
                        "height": "16px"
                    },
                    piecewiseActiveStyle: {
                        "backgroundColor": "#93278F"
                    },
                    bgStyle: {
                        "backgroundColor": "transparent",
                    },
                    processStyle: {
                        "backgroundColor": "transparent"
                    },
                    sliderStyle: [
                        {},
                        {
                            "backgroundColor": "rgba(255,183,0,0.85)",
                        }
                    ],
                },
            }
        },
        methods: {
            getFormat: function (date, format = 'MMMM') {
                return moment(date).format(format)
            },
            getPaymentSchedule: function(){
                let _this = this;
                this.$modal.show('calculator-payment-schedule');
                if( _this.productID){
                    _this.$http.get('/scheduleAndGESV', {params: {
                            term: _this.term,
                            amount: _this.amount,
                            productId: _this.productID,
                            calculationType: _this.creditType
                        }
                    }).then(
                        response => {
                            if(response.data.code === 0){
                                if( _this.paymentSchedule.length > 0){

                                }else{
                                    each(response.data.data.schedule, function (item, key) {
                                        _this.paymentSchedule.push({
                                            date: moment(+item.date.year+'-'+item.date.monthValue+'-'+item.date.dayOfMonth),
                                            amount: item.amount,
                                            remainingDebtBySchedule: item.remainingDebtBySchedule
                                        })
                                    });
                                }
                            }
                        },
                        response => {}
                    )
                }else{
                    return '';
                }
            }
        },
        watch: {
          amount: function (newVal, oldVal) {
              if( newVal !== oldVal){
                this.paymentSchedule = [];
              }
          },
          term: function (newVal, oldVal) {
              if( newVal !== oldVal){
                this.paymentSchedule = [];
              }
          },
          creditType: function (newVal, oldVal) {
              if( newVal !== oldVal){
                this.paymentSchedule = [];
              }
          }
        },
        computed: {
            ...mapFields([
                'amount',
                'amountExternalStatus',
                'amountExternalError',

                'term',
                'termExternalStatus',
                'termExternalError',

                'creditType',
                'creditTypeExternalStatus',
                'creditTypeExternalError',

                'productID'
            ]),
            currentValue: {
                get: function () {
                    return this.amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
                },
                set: function (newValue) {
                    this.amount = parseInt(newValue.replace(/[^0-9.]/g, ""));
                }
            },
            isAnnuity: function () {
                return this.creditType === 'ANNUITY'
            },
            loanResult: function () {
                const amount = this.amount;
                const months = this.term;
                // i_monthly = (1 + GESV)^(1/12) − 1 — same closed-form for both schedule types
                const monthlyRate = Math.pow(1 + TARGET_GESV, 1 / 12) - 1;
                if (this.isAnnuity) {
                    const pmt = annuityPaymentCalc(amount, monthlyRate, months);
                    return {
                        firstPayment: pmt,
                        totalInterest: pmt * months - amount,
                    };
                } else {
                    const schedule = buildDifferentiatedSchedule(amount, monthlyRate, months);
                    const totalInterest = schedule.reduce((s, r) => s + r.interest, 0);
                    return {
                        firstPayment: schedule[0].payment,
                        totalInterest,
                    };
                }
            },
            ANNUITY: function () {
                return Math.ceil(this.loanResult.firstPayment);
            },
            DIFFERENTIATED: function () {
                return Math.ceil(this.loanResult.firstPayment);
            },
            OVERPAYMENT() {
                return Math.ceil(this.loanResult.totalInterest) || 0;
            },
            GESV: function () {
                return 45.98;
            },
            monthCount: function () {
                return this.term + ' ' + Helpers.declension(['месяц', 'месяца', 'месяцев'], this.term)
            },
            returnDate: function () {
                return moment().add(1, 'months').format('D MMMM YYYY');
            },
        },
        mounted(){
        }
    }
</script>

<style lang="scss">
@import "~@/assets/scss/variable";

.loader-popup {
  height: 100%;
  min-height: 300px;
  width: 100%;
  text-align: center;
  left: 0;
  top: 0;
  z-index: 1000;
}
.modal-schedule {
  padding: 10px 0;
  height: 100%;
  min-height: 300px;
  .payment-scheduler {
    height: 100%;
    min-height: 300px;
  }
}
.payment-scheduler__loader {
  height: 300px;
  min-height: 300px;
  #coin {
    top: 65%;
  }
}
.calculator {
  max-width: 100%;
  margin: 0 10px;
  &__wrap {
    padding: 10px;
  }
  &__info-title {
    font-size: 16px;
    margin: 15px 0 5px;
  }
  &__amount {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }
  &__amount-label {
    font-size: 14px;
    color: #3f3f3f;
    font-family: $RobotoRegular;
  }
  &__amount-input-wrap {
    max-width: 45%;
  }
  &__amount-input {
    font-size: 24px;
    color: #3f3f3f;
    font-family: $RobotoRegular;
    background: none;
    border: none;
    display: inline-block;
    max-width: calc(100% - 16px);
    width: 100%;
    text-align: right;
    border-bottom: 1px solid #848484;
  }
  &__amount-icon {
    display: inline-block;
    font-size: 18px;
    margin-left: 5px;
    color: #3f3f3f;
    width: 10px;
  }

  &__slider-borders {
    display: flex;
    justify-content: space-between;
  }
  &__slider-min,
  &__slider-max {
    font-size: 10px;
    font-family: $RobotoRegular;
    color: #848484;
  }

  &__settlement-type-wrap {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    margin: 10px 0;
  }


  &__settlement-wrap {
    display: block;
    max-width: 100%;
    width: auto;
    flex: 1;
  }

  &__settlement-radio {
    padding: 2px 10px 3px;
    max-width: 100%;
    width: 100%;
    display: block;
    &:hover,
    &:focus {
      cursor: pointer;
    }
  }
  &__settlement-input:checked ~ .calculator__settlement-radio {
    font-size: 12px;
    font-family: $RobotoRegular;
    background: #f4f4f4;
    color: #70117e;
    border: 1px solid #70117e;
    border-radius: 15px;
    text-align: center;
  }
  &__settlement-input:not(:checked) ~ .calculator__settlement-radio {
    font-size: 12px;
    font-family: $RobotoRegular;
    background: #f4f4f4;
    color: #9b9b9b;
    border: 1px solid #f4f4f4;
    border-radius: 15px;
    text-align: center;
  }
  &__settlement-input {
    display: none;
  }
  &__info-wrap {
    margin-bottom: 10px;
  }
  &__info {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 5px;
  }
  &__info-label {
    font-family: $RobotoRegular;
    font-size: 14px;
    color: #3f3f3f;
  }
  &__amount-info-value {
    font-family: $RobotoRegular;
    font-size: 24px;
    text-align: right;
  }
  &__term-info-value {
    font-family: $RobotoRegular;
    font-size: 18px;
    text-align: right;
  }

  &__credit-info-wrap {
    margin-top: 10px;
    background: #f8f8f8;
    padding: 10px;
  }
  &__credit-info {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 5px;
  }
  &__credit-info-label {
    font-size: 12px;
    font-family: $RobotoRegular;
    color: #3f3f3f;
  }
  &__credit-amount {
    font-size: 20px;
    font-family: $RobotoRegular;
    color: #3f3f3f;
  }
  &__credit-date {
    font-size: 16px;
    font-family: $RobotoRegular;
    color: #3f3f3f;
  }
  &__payment-schedule {
    &:hover {
      cursor: pointer;
    }
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 14px;
    .icon {
      width: 28px;
      height: 28px;
      min-height: 28px;
      min-width: 28px;
    }
  }
}
</style>
